import { ImageSquare } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { useQuery } from 'react-query';

import {
  CoreBenefitCardStyled,
  BenefitCardHeaderStyled,
  DeductibleSectionStyled,
  AvatarSectionWrapper,
  CarrierLogoBoxStyled,
  CarrierBoxStyled,
  CarrierImageStyled,
  BenefitPlanNameStyled,
  TruncatedBenefitPlanNameStyled,
  IconBoxStyled,
  CarrierNameStyled,
} from '../CoreBenefitCardStyled';
import { SpecificIcon } from 'enrollment/utils/common/Utils';
import { Divider } from 'theSource/components';
import { CoreBenefitCardAvatars } from '../CoreBenefitCardAvatars';
import svgBackground from 'enrollment/assets/benefitLogoBackground.svg';
import { CoreBenefitCardProps } from '../../types/dtos';
import { TooltipWrapper } from 'enrollment/components/Shared/TooltipWrapper';
import { getBenefitPlanDataQueryKey } from 'theSource/features/Benefits/utils';
import { PlanDetailsSectionResponseDto } from 'theSource/features/Benefits/BenefitReviewPage/components/PlanDetails/types';
import { api } from 'theSource/utils';

const ORG_PLAN_NAME_CHARACTER_LIMIT = 20;
const CARRIER_NAME_CHARACTER_LIMIT = 10;
export const CoreBenefitCard = ({ benefit }: CoreBenefitCardProps) => {
  const { t } = useTranslation('pages.home');
  const orgPlanName = benefit.coverage.req_org_plan_name;

  const { data: planData, isLoading: isLoadingPlanDetails } =
    useQuery<PlanDetailsSectionResponseDto>(
      getBenefitPlanDataQueryKey(benefit.coverage.org_plan_id as number),
      () =>
        api.get<PlanDetailsSectionResponseDto>(
          `/org_plan/${benefit.coverage.org_plan_id}`
        ),
      { enabled: Boolean(benefit.coverage.org_plan_id) }
    );

  return (
    <CoreBenefitCardStyled
      data-testid="core-benefit-card"
      className="core-benefit-card"
    >
      <BenefitCardHeaderStyled>
        <IconBoxStyled backGroundImg={svgBackground}>
          <SpecificIcon benefitType={benefit.benefit_lookup_code} />
        </IconBoxStyled>
        <CarrierBoxStyled>
          {orgPlanName.split('').length >= ORG_PLAN_NAME_CHARACTER_LIMIT ? (
            <TooltipWrapper
              position="top"
              data-testid="tool-tip"
              tooltipContent={benefit.coverage.req_org_plan_name}
            >
              <TruncatedBenefitPlanNameStyled data-testid="truncated-org-plan-name">
                {orgPlanName}
              </TruncatedBenefitPlanNameStyled>
            </TooltipWrapper>
          ) : (
            <BenefitPlanNameStyled>{orgPlanName}</BenefitPlanNameStyled>
          )}
          <CarrierLogoBoxStyled>
            {benefit.coverage?.carrier_logo_url &&
            benefit.coverage?.carrier_logo_url !== null ? (
              <CarrierImageStyled
                src={`${benefit.coverage.carrier_logo_url}`}
                alt={benefit.coverage.req_carrier_name || 'carrier logo'}
                data-testid="carrier-logo"
              />
            ) : (
              <div className="d-flex align-items-center">
                <ImageSquare
                  size={32}
                  weight="duotone"
                  color="#56638a"
                  data-testid="default-carrier-logo"
                />
                {benefit.coverage.req_carrier_name.split('').length >=
                CARRIER_NAME_CHARACTER_LIMIT ? (
                  <TooltipWrapper
                    position="top"
                    data-testid="tool-tip"
                    tooltipContent={benefit.coverage.req_carrier_name}
                  >
                    <CarrierNameStyled data-testid="truncated-carrier-name">
                      {benefit.coverage.req_carrier_name}
                    </CarrierNameStyled>
                  </TooltipWrapper>
                ) : (
                  <Typography color="#56638a">
                    {benefit.coverage.req_carrier_name}
                  </Typography>
                )}
              </div>
            )}
          </CarrierLogoBoxStyled>
        </CarrierBoxStyled>
      </BenefitCardHeaderStyled>
      <div>
        <Divider />
      </div>
      <AvatarSectionWrapper>
        <CoreBenefitCardAvatars benefit={benefit} />
      </AvatarSectionWrapper>
      <DeductibleSectionStyled>
        {!isLoadingPlanDetails &&
          planData &&
          planData.data.org_plan_content_details.length > 0 && (
            <div>
              <div className="mb-2">
                <span>{planData.data.org_plan_content_details[0].label}</span>
              </div>

              <div className="mt-2">
                <p className="font-weight-bold">
                  {planData.data.org_plan_content_details[0].in_network
                    ? planData.data.org_plan_content_details[0].in_network
                    : ''}
                </p>
                <p className="networkPill">
                  <span>
                    {t('coreBenefits.benefitCard.annualDetails.InNetworkCaps')}
                  </span>
                </p>
              </div>
            </div>
          )}
      </DeductibleSectionStyled>
    </CoreBenefitCardStyled>
  );
};
